import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import TableChartIcon from "@mui/icons-material/TableChart";
import { GridLoader } from "react-spinners";
import Loading from "../../../../components/Loading";
import Pagination from "../../../../components/Pagination";
import ApuracaoCardShell, { TITULO_SX } from "../ApuracaoCardShell";
import SelectFiltro, { BuscaFiltro, FILTER_FIELD_SX } from "../SelectFiltro";
import { STATUS_APURACAO_OPTS } from "../../constants";
import { exibirCodRelatorio, anosNoPeriodo, formatarCnpj } from "../../utils";
import {
  podeAbrirDetalheSumarizacao,
  podeProcessarSumarizacao,
} from "../../Detalhe/utils/agregarPorConta.util";
import TiposApuracaoAutocomplete from "../TiposApuracaoAutocomplete";
import StatusChip from "./StatusChip";
import styles from "./styles.module.css";
import {
  usePermissoes,
  CHAVES_APURACOES_CREATE,
  CHAVES_APURACOES_EDIT,
  CHAVES_ROTA_PROCESSAMENTO,
} from "../../../../config/Context/PermissoesContext";

// Referência estável (não recriar por render) — TiposApuracaoAutocomplete.isOptionEqualToValue
// cai em comparação por referência (===) pra opções sem idRelatorio, como esta.
const TODOS_TIPO_OPCAO = { codigoRelatorio: "", label: "Todos" };

/**
 * Canvas branco da página — a rota (Apuracoes/index.js) não tem nenhum wrapper próprio;
 * antes desta tela usava o `.container` do ApuracaoCardShell (fundo #f5f7fa + margem) como
 * ÚNICO elemento cobrindo o fundo azul do app. Com título/filtros renderizando ANTES do
 * ApuracaoCardShell, essa área ficava sem fundo sólido e o azul aparecia atrás do texto.
 * Este Box reproduz a mesma margem/raio/sombra do `.container` (mesmos valores de
 * TableCard/styles.module.css), só que branco — título/filtros ficam "soltos" no canvas,
 * e o card de filtros (cinza) e o card da tabela (ApuracaoCardShell com hideContainer)
 * ficam por dentro, sem duplicar a moldura.
 */
const PAGE_CANVAS_SX = {
  margin: "0 2rem",
  padding: "20px 24px 24px",
  borderRadius: "12px",
  backgroundColor: "#fff",
  boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
};

function fmtAnoIntervalo(periodoInicio, periodoFim) {
  const anos = anosNoPeriodo(periodoInicio, periodoFim);
  if (!anos.length) return "—";
  const ini = anos[0];
  const fim = anos[anos.length - 1];
  return ini === fim ? String(ini) : `${ini} – ${fim}`;
}

// TXM-325 item 11 — coluna Ações com 4 ícones em sequência fixa:
// (1) sumarização (detalhe OU processar) · (2) adicionar período · (3) reprocessar · (4) histórico.
function AcoesLinha({
  row,
  onDetalhe,
  onAdicionar,
  onProcessamento,
  onProcessarSumarizacao,
  onReprocessar,
  processandoSumarizacao,
  podeCriar,
  podeEditar,
  podeVerProcessamento,
}) {
  const apuracaoEmProcessamento = row.status === "processando";
  // Backend bloqueia "adicionar período" (STATUS_INDISPONIVEL) e "reprocessar"
  // (STATUS_PERMITE_REPROCESSAR não inclui "cancelado") para apuração cancelada — sem esta
  // checagem, os botões ficavam habilitados e o usuário só descobria via 409 do backend.
  const apuracaoCancelada = row.status === "cancelado";
  const podeDetalhes = podeAbrirDetalheSumarizacao(row);
  const podeProcessar = podeEditar && podeProcessarSumarizacao(row);
  const processando = processandoSumarizacao === row.id;
  const tooltipApuracaoProcessando = "Aguarde a conclusão da apuração";
  const tooltipApuracaoCancelada = "Apuração cancelada";
  const iconSx = { color: "#1b3a5f" };

  return (
    <div className={styles.acoesCell}>
      {/* (1) Sumarização — detalhe (olho) OU processar (play), contextual */}
      {podeDetalhes ? (
        <Tooltip title="Detalhe da sumarização" arrow>
          <span>
            <IconButton
              size="small"
              onClick={() => onDetalhe(row)}
              sx={iconSx}
              aria-label="Detalhe da sumarização"
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip
          title={
            apuracaoEmProcessamento
              ? tooltipApuracaoProcessando
              : row.sumarizacaoStatus === "erro"
                ? "Tentar sumarização novamente"
                : "Processar sumarização"
          }
          arrow
        >
          <span>
            <IconButton
              size="small"
              disabled={apuracaoEmProcessamento || processando || !podeProcessar}
              onClick={() => onProcessarSumarizacao(row)}
              sx={iconSx}
              aria-label="Processar sumarização"
            >
              {processando ? (
                <CircularProgress size={16} sx={iconSx} />
              ) : (
                <PlayArrowOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* (2) Adicionar período — desabilitado em erro/processando */}
      {podeCriar && (
        <Tooltip
          title={
            apuracaoEmProcessamento
              ? tooltipApuracaoProcessando
              : apuracaoCancelada
                ? tooltipApuracaoCancelada
                : "Adicionar período"
          }
          arrow
        >
          <span>
            <IconButton
              size="small"
              disabled={apuracaoEmProcessamento || apuracaoCancelada || row.status === "erro"}
              onClick={() => onAdicionar(row)}
              sx={iconSx}
              aria-label="Adicionar período"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* (3) Reprocessar — limpa a base do(s) ano(s) e regenera (item 08); desabilitado em processando/cancelado */}
      {podeEditar && (
        <Tooltip
          title={
            apuracaoEmProcessamento
              ? tooltipApuracaoProcessando
              : apuracaoCancelada
                ? tooltipApuracaoCancelada
                : "Reprocessar anos"
          }
          arrow
        >
          <span>
            <IconButton
              size="small"
              disabled={apuracaoEmProcessamento || apuracaoCancelada}
              onClick={() => onReprocessar?.(row)}
              sx={iconSx}
              aria-label="Reprocessar anos"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* (4) Histórico → processamento */}
      {podeVerProcessamento && (
        <Tooltip title="Histórico / processamento" arrow>
          <IconButton
            size="small"
            onClick={() => onProcessamento(row)}
            sx={iconSx}
            aria-label="Histórico / processamento"
          >
            <TableChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
}

export default function ApuracoesListagem({
  loading,
  loadingTable,
  rows,
  total,
  page,
  limit,
  onPageChange,
  onLimitChange,
  filtroEmpresa,
  onFiltroEmpresaChange,
  filtroCnpj,
  onFiltroCnpjChange,
  filtroTipo,
  onFiltroTipoChange,
  filtroStatus,
  onFiltroStatusChange,
  onRefresh,
  onDetalhe,
  onAdicionar,
  onProcessamento,
  onProcessarSumarizacao,
  onReprocessar,
  processandoSumarizacaoId,
  onNovaApuracao,
  tiposApuracao = [],
  loadingTiposApuracao = false,
}) {
  const { pode } = usePermissoes();
  const podeCriar = pode(CHAVES_APURACOES_CREATE);
  const podeEditar = pode(CHAVES_APURACOES_EDIT);
  const podeVerProcessamento = pode(CHAVES_ROTA_PROCESSAMENTO);
  const showInitialLoading = loading && rows.length === 0;
  // TODOS_TIPO_OPCAO garante que o Autocomplete de Tipo nunca fique com value:null (mesma
  // convenção de SelectFiltro, cujo options[0] é sempre "Todos") — sem isso, com nenhum tipo
  // selecionado, o label não flutua (fica "dentro" do campo em vez de na borda), diferente
  // dos demais filtros desta tela e das abas.
  const tiposApuracaoComTodos = [TODOS_TIPO_OPCAO, ...tiposApuracao];
  const tipoSelecionado =
    tiposApuracaoComTodos.find((t) => t.codigoRelatorio === filtroTipo) ?? TODOS_TIPO_OPCAO;

  return (
    <Box sx={PAGE_CANVAS_SX}>
      {/* Cabeçalho: título/subtítulo à esquerda (mesma tipografia da tela de Usuários),
          botão de ação alinhado ao topo do título à direita — igual ao protótipo. */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={TITULO_SX}>
            Apurações
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
            Pedidos de apuração realizados por empresa e tipo
          </Typography>
        </Box>
        {podeCriar && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onNovaApuracao}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#18316e",
              minHeight: 40,
              whiteSpace: "nowrap",
              px: 2,
              "&:hover": {
                bgcolor: "#152858",
              },
            }}
          >
            Nova apuração
          </Button>
        )}
      </Box>

      {/* Filtros no mesmo padrão das abas (SelectFiltro/BuscaFiltro — label flutuante dentro
          do campo, não rótulo estático em cima), em vez do layout próprio que esta tela tinha
          antes. Card cinza (fundo levemente mais escuro que o branco da tabela) mantido. */}
      <Box sx={{ bgcolor: "#f5f7fa", borderRadius: "12px", p: 2.5, mb: 2, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
        <BuscaFiltro
          label="Empresa"
          placeholder="Filtrar por nome..."
          value={filtroEmpresa}
          onChange={onFiltroEmpresaChange}
        />
        <BuscaFiltro
          label="CNPJ"
          placeholder="Somente números ou formatado"
          value={filtroCnpj}
          onChange={onFiltroCnpjChange}
        />
        <TiposApuracaoAutocomplete
          label="Tipo"
          options={tiposApuracaoComTodos}
          value={tipoSelecionado}
          onChange={(_, option) =>
            onFiltroTipoChange(option?.codigoRelatorio ?? "")
          }
          loading={loadingTiposApuracao}
          sx={FILTER_FIELD_SX}
          placeholder="Todos"
        />
        <SelectFiltro
          label="Status da Apuração"
          value={filtroStatus}
          onChange={onFiltroStatusChange}
          options={STATUS_APURACAO_OPTS}
        />
        <Tooltip title="Atualizar">
          <IconButton
            size="small"
            onClick={onRefresh}
            sx={{ color: "#1b3a5f" }}
            aria-label="Atualizar listagem"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <ApuracaoCardShell
        hideContainer
        innerCardSx={{ margin: 0, border: "none" }}
        paginacao={
          !showInitialLoading && (
            <Pagination
              paginationKey="apuracoes-listagem"
              serverSideTotal={total}
              serverSidePage={page}
              serverSideRowsPerPage={limit}
              onServerPageChange={onPageChange}
              onServerRowsPerPageChange={onLimitChange}
              disabled={loadingTable}
            />
          )
        }
      >
      {showInitialLoading ? (
        <div className={styles.loadingWrap}>
          <Loading loading />
        </div>
      ) : (
        <div className={styles.tableSection}>
          {loadingTable && (
            <div className={styles.tableOverlay} aria-hidden="true">
              <GridLoader color="#1b3a5f" loading size={8} aria-label="Carregando" />
            </div>
          )}
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.colEmpresa}>Empresa</th>
                <th className={styles.colCnpj}>CNPJ</th>
                <th className={styles.colTipo}>Tipo</th>
                <th className={styles.colPeriodo}>Competência</th>
                <th className={styles.colStatus}>Situação</th>
                <th className={styles.colAcoes}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className={styles.emptyRow}>
                  <td colSpan={6}>Nenhum registro encontrado</td>
                </tr>
              ) : (
                rows.map((row) => {
                  const codRelatorio = exibirCodRelatorio(row.tipo);
                  return (
                  <tr key={row.id ?? `${row.cnpj}-${row.periodoInicio}`}>
                    <td className={styles.colEmpresa}>
                      <Tooltip title={row.empresa} arrow placement="top">
                        <Box
                          component="span"
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100%",
                          }}
                        >
                          {row.empresa}
                        </Box>
                      </Tooltip>
                    </td>
                    <td className={styles.colCnpj}>{formatarCnpj(row.cnpj)}</td>
                    <td className={styles.colTipo}>
                      <Tooltip title={codRelatorio} arrow placement="top">
                        <Box
                          component="span"
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "100%",
                          }}
                        >
                          {codRelatorio}
                        </Box>
                      </Tooltip>
                    </td>
                    <td className={styles.colPeriodo}>{fmtAnoIntervalo(row.periodoInicio, row.periodoFim)}</td>
                    <td className={styles.colStatus}>
                      <StatusChip status={row.status} />
                    </td>
                    <td className={styles.colAcoes}>
                      <AcoesLinha
                        row={row}
                        onDetalhe={onDetalhe}
                        onAdicionar={onAdicionar}
                        onProcessamento={onProcessamento}
                        onProcessarSumarizacao={onProcessarSumarizacao}
                        onReprocessar={onReprocessar}
                        processandoSumarizacao={processandoSumarizacaoId}
                        podeCriar={podeCriar}
                        podeEditar={podeEditar}
                        podeVerProcessamento={podeVerProcessamento}
                      />
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
      </ApuracaoCardShell>
    </Box>
  );
}
